import { useEffect, useMemo, useRef, useState } from 'react';
import Card from './Card';
import { CategoryInterface } from '../../../services/exports/Interfaces';
import SideBarIcon from '../../../assets/logo/SideBarIcon';
import useTheme from '../../../hooks/ui/useTheme';
import MenuSideBar from './MenuSideBar';
import useMenu from '../../../hooks/menu/useMenu';
import SearchInput from './SearchInput/SearchInput';
import classNames from 'classnames';
import useScreenType from '../../../hooks/utility/useScreenType';
import ScrollMenu from './ScrollMenu';
import { useSearchParams } from 'react-router-dom';
import SearchIcon from '../../../assets/icons/actions/SearchIcon';
import { MENU_TAB_CONTENT_ID } from '../../../modules/mobile/pages/tabs/menu';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import { collect } from 'collect.js';
import useUrlParams from '../../../hooks/utility/useUrlParams';

interface Props {
  focusedCategoryId?: number;
  categories?: CategoryInterface[];
}

const MenuBar = ({ focusedCategoryId, categories }: Props) => {
  const theme = useTheme();
  const { menus, selectedMenu } = useMenu();

  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [showSideBar, setShowSideBar] = useState<boolean>(false);
  const { isMobile } = useScreenType();
  const { behavior, categoryId } = useUrlParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasSearchInput = !isMobile || (isMobile && searchParams.has('search'));
  const hasCategories = !!categories?.length && (!isMobile || (isMobile && !searchParams.has('search')));
  const menuBarRef = useRef<HTMLDivElement>();
  const { isMobileApp } = useMobileApp();

  const onClickSearch = (): void => {
    searchParams.append('search', '');
    setSearchParams(searchParams);
  };

  useEffect(() => {
    selectedMenu && setSelectedMenuId(selectedMenu?.id);

    selectedMenuId &&
      selectedMenuId !== selectedMenu?.id &&
      menus.firstWhere('id', selectedMenuId) &&
      categories?.length > 0 &&
      jumpToCategory(categories[0].id);
  }, [selectedMenu?.id]);

  function toggleSideBar(): void {
    setShowSideBar(!showSideBar);
  }

  const handleHashChange = (): void => {
    new Promise((resolve, reject) => {
      if (!categoryId) {
        return reject(true);
      }
      jumpToCategory(categoryId, behavior);
      resolve(true);
    })
      .then(() => {
        window.history.replaceState({}, '', window.location.pathname);
      })
      .catch(() => {});
  };

  useEffect(() => {
    handleHashChange();
  }, [categoryId]);

  function jumpToCategory(id?: number, behavior: ScrollBehavior = 'smooth'): void {
    const category = document.getElementById(`category-id-${id}`);

    if (isMobileApp) {
      const scrollableEl = document.getElementById(MENU_TAB_CONTENT_ID) as HTMLIonContentElement;

      void scrollableEl.scrollToPoint(undefined, category.offsetTop - menuBarRef.current.scrollHeight - 24, 600);
      return;
    }

    const elementPosition = category.getBoundingClientRect().top;
    const top = elementPosition + window.scrollY - menuBarRef.current.scrollHeight - 24;

    window.scrollTo({
      left: 0,
      top,
      behavior,
    });
  }

  const renderSideBar = useMemo<JSX.Element>(() => {
    return (
      <MenuSideBar
        categories={categories}
        open={showSideBar}
        toggle={setShowSideBar}
        focusedCategoryId={focusedCategoryId}
        onSelectCategory={(category) => jumpToCategory(category?.id)}
      />
    );
  }, [showSideBar, focusedCategoryId]);

  const renderMenuButton = (): JSX.Element => (
    <button
      className={classNames(
        'flex flex-row p-2xs lg:mr-sm bg-brand-background-primary border border-brand-border rounded-full items-center justify-center shrink-0 w-[32px] h-[32px]',
        {
          'rounded-full size-8 border mx-md justify-center': isMobile,
        },
      )}
      onClick={toggleSideBar}
      id={'menu-button'}
    >
      <SideBarIcon color={theme.color.brand.icon.color} className="h-[12px]" />
    </button>
  );

  const activeCategoryId = useMemo(
    () => focusedCategoryId ?? collect(categories)?.first()?.id,
    [focusedCategoryId, categories],
  );

  return (
    <div
      className="bg-brand-background-primary relative h-fit py-xs flex flex-col space-y-sm"
      id="menu-bar"
      ref={menuBarRef}
    >
      {hasSearchInput && <SearchInput className={classNames({ 'px-sm': isMobile })} />}
      {hasCategories && (
        <div className="h-full w-full flex flex-row items-center relative lg:px-none px-sm">
          {isMobile && (
            <button
              className="size-8 border rounded-full flex justify-center items-center shrink-0 shadow-icon dark:shadow-none"
              onClick={onClickSearch}
            >
              <SearchIcon className="size-4" />
            </button>
          )}
          {!isMobileApp && renderMenuButton()}

          {categories?.length > 0 && (
            <ScrollMenu focusedCategoryId={activeCategoryId}>
              {categories?.map((category: CategoryInterface) => (
                <Card
                  key={category?.id?.toString()}
                  itemId={category?.id?.toString()}
                  category={category}
                  focusedCategoryId={activeCategoryId}
                  onClick={jumpToCategory}
                />
              ))}
            </ScrollMenu>
          )}
        </div>
      )}
      {renderSideBar}
    </div>
  );
};

export default MenuBar;
